import { useEffect, useMemo, useState } from 'react';
import { scopaPartnerOf, scopaTeamOf, sortHand, type ScopaLegal, type ScopaTeamRound, type ScopaView } from '@cardhub/engine';
import { PlayingCard, shortCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { SeatView, MySeat, useNarrow, useSpeech } from './common';

export function ScopaTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as ScopaView;
  const legal = useGame((s) => s.legal) as ScopaLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const narrow = useNarrow();
  const [picked, setPicked] = useState<string | null>(null);
  const [preview, setPreview] = useState<string[]>([]);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const myTeam = mySeat !== null ? scopaTeamOf(mySeat) : 0;
  const moves = new Map((legal?.moves ?? []).map((m) => [m.card, m.captures]));
  const options = picked ? (moves.get(picked) ?? []) : [];
  const last = view.lastMove;
  const roundEnd = view.phase === 'ROUND_END' || view.phase === 'MATCH_END';
  const lastRound = view.roundResults.at(-1);

  useEffect(() => {
    setPicked(null);
    setPreview([]);
  }, [view.turn, view.round, view.phase]);

  const pick = (c: string) => {
    if (!myTurn || acting) return;
    if (picked === c) {
      setPicked(null);
      setPreview([]);
      return;
    }
    setPicked(c);
    setPreview(moves.get(c)?.[0] ?? []);
  };

  const confirm = (capture: string[]) => {
    if (!picked) return;
    void act({ type: 'play', card: picked, capture });
  };

  const teamPill = (team: number) => (
    <span className={`pill team-pill ${team === myTeam ? 'mine' : ''}`} key={team}>
      {team === myTeam ? t('sp.us') : t('sp.them')}: {view.teamScores[team] ?? 0} · {t('bh.cards', { n: view.capturedCounts[team] ?? 0 })}{(view.scopas[team] ?? 0) > 0 ? ` · ${view.scopas[team]}★` : ''}
    </span>
  );

  const row = (label: string, a: string | number, b: string | number, win?: [boolean, boolean]) => (
    <tr key={label}>
      <td>{label}</td>
      {[a, b].map((v, i) => (
        <td key={i} className="num" style={{ color: win?.[i] ? 'var(--accent)' : undefined }}>
          {v}
        </td>
      ))}
    </tr>
  );

  const breakdown = (res: ScopaTeamRound[]) => {
    const [x, y] = [res[myTeam]!, res[1 - myTeam]!];
    return (
      <table className="scoreboard" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th />
            <th>{t('sp.us')}</th>
            <th>{t('sp.them')}</th>
          </tr>
        </thead>
        <tbody>
          {row(t('sc.cards'), `${x.cards}`, `${y.cards}`, [x.points.cards > 0, y.points.cards > 0])}
          {row(t('sc.diamonds'), `${x.diamonds}`, `${y.diamonds}`, [x.points.diamonds > 0, y.points.diamonds > 0])}
          {row(t('sc.settebello'), x.settebello ? '✓' : '–', y.settebello ? '✓' : '–', [x.points.settebello > 0, y.points.settebello > 0])}
          {row(t('sc.primiera'), `${x.primiera}`, `${y.primiera}`, [x.points.primiera > 0, y.points.primiera > 0])}
          {row(t('sc.scopas'), `${x.scopas}`, `${y.scopas}`, [x.points.scopas > 0, y.points.scopas > 0])}
          {row(t('sc.total'), `+${x.total}`, `+${y.total}`)}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[scopaTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === scopaPartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {!roundEnd && <span className="tag">{view.handCounts[s]}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered" style={{ gap: 8 }}>
          <div className="center-note">
            {t('sp.round', { n: view.round })} · {t('sc.deck', { n: view.deckCount })}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 6, flexWrap: 'wrap' }} aria-label={t('table.scores')}>
            {teamPill(myTeam)}
            {teamPill(1 - myTeam)}
          </div>
          {roundEnd && lastRound ? (
            <div className="card glass" style={{ width: '90%', maxHeight: '44vh', overflow: 'auto' }} role="status">
              <strong>{t('sc.dealResult', { n: view.roundResults.length })}</strong>
              {breakdown(lastRound)}
            </div>
          ) : (
            <div className="row" style={{ flexWrap: 'wrap', gap: 4, justifyContent: 'center', maxWidth: '88%', marginTop: 16 }} aria-label={t('sc.table')}>
              {view.table.length === 0 && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('sc.empty')}</span>}
              {view.table.map((c) => (
                <PlayingCard key={c} id={c} size={narrow ? 'sm' : 'md'} selected={preview.includes(c)} />
              ))}
            </div>
          )}
          {last && !roundEnd && (
            <span className={`pill ${last.scopa ? 'accent' : ''}`} role="status">
              {name(last.seat)}: {last.captured.length > 0 ? t('sc.captured', { card: shortCard(last.card), n: last.captured.length }) : t('sc.trailed', { card: shortCard(last.card) })}
              {last.scopa ? ` — ${t('sc.scopa')}` : ''}
            </span>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && (
          <div className="action-bar" role="group" aria-label={t('sc.choose')}>
            {!picked && <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)' }}>{t('sc.pickCard')}</span>}
            {picked && options.length === 0 && (
              <button className="btn primary" disabled={acting} onClick={() => confirm([])}>
                {t('sc.trail', { card: shortCard(picked) })}
              </button>
            )}
            {picked &&
              options.map((o, i) => (
                <button key={i} className="btn primary" disabled={acting} onMouseEnter={() => setPreview(o)} onFocus={() => setPreview(o)} onClick={() => confirm(o)}>
                  {t('sc.capture')} {o.map(shortCard).join(' + ')}
                </button>
              ))}
          </div>
        )}
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.7}>
          {hand.map((c, i) => (
            <PlayingCard key={c} id={c} size={narrow ? 'md' : 'lg'} dealIndex={view.deckCount >= 24 && view.table.length === 4 && !view.lastMove ? i : undefined} selected={picked === c} playable={Boolean(myTurn)} onClick={() => pick(c)} />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{`${name(team)} & ${name(team + 2)}`}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((r, i) => (
              <tr key={i}>
                <td>D{i + 1}</td>
                {r.map((x, team) => (
                  <td key={team} className="num">+{x.total}</td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('sc.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}
